import { recipesCopy } from '@/copy'
import { fillSlots } from '@/copy/slots'
import { Button } from '@/ui/Button/Button'
import { EmptyState } from '@/ui/EmptyState/EmptyState'
import { Field, Select } from '@/ui/Field/Field'
import { Icon } from '@/ui/Icon/Icon'
import { Link } from '@/ui/Link/Link'
import type { RecipeView } from '../../api/recipes'
import { ROUTES } from '../../constants/routes'
import styles from './SourcePicker.module.css'

interface Props {
  workspaceId: string
  recipe: RecipeView
  value: string
  onValue: (sourceId: string) => void
}

/**
 * Where a recipe takes its events from: the workspace's resources it can
 * use. One that still needs setup is shown, disabled, with what it needs.
 */
export function SourcePicker({ workspaceId, recipe, value, onValue }: Props) {
  const label = recipe.source.label
  if (recipe.candidates.length === 0) {
    return (
      <EmptyState
        variant="inline"
        description={recipesCopy.setup.noSource(label)}
        action={
          <Button
            variant="ghost"
            ground="surface"
            href={ROUTES.resourceNew(workspaceId)}
          >
            <Icon name="plus" size="icon-embedded" />
            {recipesCopy.setup.addSource}
          </Button>
        }
      />
    )
  }
  const waiting = recipe.candidates.filter(
    (c) => c.readiness.state === 'needs_setup',
  )
  return (
    <Field
      id={`recipe-${recipe.id}-source`}
      label={label.charAt(0).toUpperCase() + label.slice(1)}
      help={
        waiting.length > 0
          ? waiting.map((c) => (
              <span className={styles.waiting} key={c.id}>
                {fillSlots(recipesCopy.setup.needsSetup, {
                  name: (
                    <Link href={ROUTES.resourceDetail(c.id)}>{c.name}</Link>
                  ),
                  reason: c.readiness.reason ?? '',
                })}
              </span>
            ))
          : undefined
      }
    >
      <Select value={value} onChange={(e) => onValue(e.target.value)}>
        <option value="">{recipesCopy.setup.chooseSource}</option>
        {recipe.candidates.map((c) => (
          <option
            key={c.id}
            value={c.id}
            disabled={c.readiness.state !== 'ready'}
          >
            {c.name}
          </option>
        ))}
      </Select>
    </Field>
  )
}
